import { AfterViewInit, Directive, ElementRef, OnInit, computed, forwardRef, inject, input, signal } from '@angular/core';

import { FormFieldControlAdapter } from './form-field-control-adapter';

@Directive({
  selector: 'input[formFieldControl], textarea[formFieldControl], select[formFieldControl]',
  standalone: true,
  providers: [{ provide: FormFieldControlAdapter, useExisting: forwardRef(() => FormFieldControlDirective) }],
  host: {
    '[attr.id]': 'id()',
    '[attr.aria-labelledby]': 'ariaLabelledBy()',
    '[attr.aria-describedby]': 'ariaDescribedBy()',
    '[attr.aria-invalid]': 'ariaInvalid()',
    '[attr.aria-required]': 'ariaRequired()',
  },
})
export class FormFieldControlDirective extends FormFieldControlAdapter implements OnInit, AfterViewInit {
  private static readonly defaultAutocomplete = 'off';

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly generatedId = this.generateControlId();
  private readonly initialHostId = this.readInitialHostId();

  readonly idInput = input<string | null>(null, { alias: 'id' });
  private readonly fallbackHostId = signal<string | null>(null);
  private readonly labelledByIdsState = signal<string[]>([]);
  private readonly describedByIdsState = signal<string[]>([]);
  private readonly invalidState = signal(false);
  private readonly requiredState = signal(false);
  private readonly disabledState = signal<boolean | null>(null);
  private readonly initialDisabledState = signal<boolean | null>(null);

  readonly id = computed(() => this.idInput() ?? this.initialHostId ?? this.fallbackHostId() ?? this.generatedId);
  readonly labelForId = computed(() => this.id());
  readonly ariaLabelledBy = computed(() => {
    const ids = this.labelledByIdsState();
    return ids.length > 0 ? ids.join(' ') : null;
  });
  readonly ariaDescribedBy = computed(() => {
    const ids = this.describedByIdsState();
    return ids.length > 0 ? ids.join(' ') : null;
  });
  readonly ariaInvalid = computed(() => (this.invalidState() ? 'true' : null));
  readonly ariaRequired = computed(() => (this.requiredState() ? 'true' : null));

  ngOnInit(): void {
    this.applyDefaultAutocomplete();
  }

  ngAfterViewInit(): void {
    const hostId = this.elementRef.nativeElement.getAttribute('id')?.trim();
    if (hostId) {
      this.fallbackHostId.set(hostId);
    }

    this.initialDisabledState.set(this.readHostDisabledState());

    this.syncDisabledState();
  }

  setLabelledByIds(ids: string[]): void {
    this.labelledByIdsState.set(ids.filter((id) => id.trim().length > 0));
  }

  setDescribedByIds(ids: string[]): void {
    this.describedByIdsState.set(ids.filter((id) => id.trim().length > 0));
  }

  setInvalid(isInvalid: boolean): void {
    this.invalidState.set(isInvalid);
  }

  setRequired(isRequired: boolean): void {
    this.requiredState.set(isRequired);
  }

  setDisabled(isDisabled: boolean | null): void {
    this.disabledState.set(isDisabled);
    this.syncDisabledState();
  }

  override focus(): void {
    this.elementRef.nativeElement.focus();
  }

  private syncDisabledState(): void {
    const element = this.elementRef.nativeElement as HTMLElement & { disabled?: boolean };
    const disabledOverride = this.disabledState();
    if (disabledOverride === null) {
      return;
    }

    const resolvedDisabled = disabledOverride || this.initialDisabledState() === true;
    if ('disabled' in element) {
      element.disabled = resolvedDisabled;
    }

    if (resolvedDisabled) {
      element.setAttribute('disabled', '');
      return;
    }

    element.removeAttribute('disabled');
  }

  private readHostDisabledState(): boolean {
    const element = this.elementRef.nativeElement as HTMLElement & { disabled?: boolean };
    if ('disabled' in element) {
      return Boolean(element.disabled);
    }

    return element.hasAttribute('disabled');
  }

  private readInitialHostId(): string | null {
    const hostId = this.elementRef.nativeElement.getAttribute('id')?.trim();
    return hostId && hostId.length > 0 ? hostId : null;
  }

  private applyDefaultAutocomplete(): void {
    const element = this.elementRef.nativeElement;
    if (element.getAttribute('autocomplete') === null) {
      element.setAttribute('autocomplete', FormFieldControlDirective.defaultAutocomplete);
    }
  }
}
